import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { ExpandedState } from '@tanstack/react-table';

import { Dimensions } from 'components/insights/constants';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';
import SegmentedControl from 'components/shared/SegmentedControl';
import {
  useInboundReferralsInsightsActions,
  useInboundReferralsInsightsStore,
} from 'stores/inboundReferralsInsightsStore';

import { subRowStyle } from '../../shared/slideout/expandableRowStyles';
import {
  getInboundExportParams,
  getInboundMetricExportParams,
  getInboundSlideoutViewByOptions,
  INBOUND_DIMENSION_EXPORT_SLUGS,
  INBOUND_DIMENSION_LABELS,
  INBOUND_METRIC_DRILL_EXPORT_SLUGS,
  INBOUND_METRIC_DRILL_VIEW_BY_OPTIONS,
  slugify,
} from '../constants';
import InboundReferralApiExport from '../InboundReferralApiExport';
import InboundReferralDetailsTable from '../table/InboundReferralDetailsTable';

import { INBOUND_METRIC_DRILLS } from './inboundMetricDrills';

type Props = {
  loading: boolean;
  data: AnalyticsResponseData;
  rollups: AnalyticsResponseRollupCollection;
  extraFilters: Record<string, unknown> | null;
};

// The slideout's breakdown table: a dynamic View By (the other dimensions) over the same table
// shape as the landing page, scoped to the open slideout's entity. A built metric drill supplies its
// own columns/rows (and expandable reason sub-rows) via INBOUND_METRIC_DRILLS; everything else uses
// the shared determination-rate table with its default (non-clickable) entity cell.
export default function InboundSlideoutTable({ loading, data, rollups, extraFilters }: Props) {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);
  const { setSlideoutDimension } = useInboundReferralsInsightsActions();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const viewByOptions = useMemo(() => {
    if (!slideoutContext) return [];
    const dims =
      slideoutContext.kind === 'entity'
        ? getInboundSlideoutViewByOptions(slideoutContext.dimension)
        : INBOUND_METRIC_DRILL_VIEW_BY_OPTIONS;
    return dims.map((d) => ({ label: INBOUND_DIMENSION_LABELS[d] ?? '', value: d }));
  }, [slideoutContext]);

  // A built metric drill (Accepted / Denied) dispatches to its own table config; every other case
  // falls back to the shared determination-rate table below.
  const drill = slideoutContext?.kind === 'metric' ? INBOUND_METRIC_DRILLS[slideoutContext.metric] : undefined;
  const drillTable = useMemo(() => {
    if (!drill || !slideoutDimension) return null;
    const header = INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '';
    return drill.buildTable({ data, rollups, entityHeader: header });
  }, [drill, slideoutDimension, data, rollups]);

  const exportSlot = useMemo(() => {
    if (!slideoutContext || !slideoutDimension) return null;
    const viewBySlug = INBOUND_DIMENSION_EXPORT_SLUGS[slideoutDimension] ?? '';
    const drillSlug =
      slideoutContext.kind === 'metric'
        ? INBOUND_METRIC_DRILL_EXPORT_SLUGS[slideoutContext.metric]
        : slugify(slideoutContext.entityName);
    const params =
      slideoutContext.kind === 'metric'
        ? getInboundMetricExportParams(slideoutContext.metric, slideoutDimension)
        : getInboundExportParams(slideoutDimension);
    return (
      <InboundReferralApiExport
        params={params}
        extraFilters={extraFilters}
        filename={`inbound-referral-${drillSlug}-by-${viewBySlug}`}
        disabled={loading}
      />
    );
  }, [slideoutContext, slideoutDimension, extraFilters, loading]);

  if (!slideoutDimension) return null;

  const entityHeader = INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '';

  return (
    <Wrapper>
      {(viewByOptions.length > 1 || exportSlot) && (
        <ViewByRow>
          {viewByOptions.length > 1 && (
            <>
              <ViewByLabel>View By:</ViewByLabel>
              <SegmentedControl
                options={viewByOptions}
                selectedValue={slideoutDimension}
                onChange={(val) => {
                  setSlideoutDimension(val as Dimensions);
                  setExpanded({});
                }}
                mobileFullWidth
              />
            </>
          )}
          {exportSlot && <ExportSlot>{exportSlot}</ExportSlot>}
        </ViewByRow>
      )}
      {drillTable ? (
        <DataTable
          columns={drillTable.columns}
          // Empty while loading so DataTable shows its height-preserving placeholder instead of the
          // retained previous View By's rows (this table changes per View By).
          data={loading ? [] : drillTable.rows}
          defaultSortBy={drillTable.defaultSortBy}
          loading={loading}
          hasFooter={false}
          maxHeight='520px'
          freezeFirstColumn
          getSubRows={drillTable.getSubRows}
          expanded={drillTable.expandable ? expanded : undefined}
          onExpandedChange={drillTable.expandable ? setExpanded : undefined}
          rowStyle={drillTable.expandable ? subRowStyle : undefined}
        />
      ) : (
        <InboundReferralDetailsTable
          loading={loading}
          data={data}
          entityHeader={entityHeader}
          dimension={slideoutDimension}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }

  /* Stretch the divider cell to the full row height so its border spans multi-line rows
     (e.g. a wrapped entity name) instead of stopping at its own content height. */
  td:first-child {
    align-self: stretch;
  }
`;

const ViewByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const ExportSlot = styled.div`
  margin-left: auto;
  align-self: flex-start;
  display: flex;
  align-items: center;
`;

const ViewByLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};

  @media (max-width: 800px) {
    display: none;
  }
`;
